<script setup>
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import DatePicker from 'dashboard/components/carBooking/DatePicker.vue';
import FormField from './FormField.vue';
import NamesList from './NamesList.vue';

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const KEY = 'CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.HOTEL';

const guests = () => props.modelValue.guests || {};

const set = patch =>
  emit('update:modelValue', { ...props.modelValue, ...patch });

const setNested = (key, patch) =>
  set({ [key]: { ...(props.modelValue[key] || {}), ...patch } });

const boardOptions = ['room_only', 'bb', 'hb', 'fb', 'ai'].map(value => ({
  value,
  label: t(`${KEY}.BOARD_TYPE_OPTIONS.${value.toUpperCase()}`),
}));
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="grid grid-cols-2 gap-2">
      <Input
        size="sm"
        :label="t(`${KEY}.NAME`)"
        :placeholder="t(`${KEY}.NAME_PLACEHOLDER`)"
        :model-value="modelValue.hotelName"
        :message="errors.hotelName"
        :message-type="errors.hotelName ? 'error' : 'info'"
        @update:model-value="set({ hotelName: $event })"
      />
      <Input
        size="sm"
        :label="t(`${KEY}.CITY`)"
        :placeholder="t(`${KEY}.CITY_PLACEHOLDER`)"
        :model-value="modelValue.city"
        @update:model-value="set({ city: $event })"
      />
      <FormField :label="t(`${KEY}.CHECK_IN`)" :error="errors.checkIn">
        <DatePicker
          :model-value="modelValue.checkIn"
          @update:model-value="set({ checkIn: $event })"
        />
      </FormField>
      <FormField :label="t(`${KEY}.CHECK_OUT`)" :error="errors.checkOut">
        <DatePicker
          :model-value="modelValue.checkOut"
          :min="modelValue.checkIn"
          @update:model-value="set({ checkOut: $event })"
        />
      </FormField>
      <Input
        size="sm"
        :label="t(`${KEY}.ROOM_TYPE`)"
        :placeholder="t(`${KEY}.ROOM_TYPE_PLACEHOLDER`)"
        :model-value="modelValue.roomType"
        @update:model-value="set({ roomType: $event })"
      />
      <Input
        size="sm"
        type="number"
        min="1"
        :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.ROOMS')"
        :model-value="modelValue.rooms"
        @update:model-value="set({ rooms: $event })"
      />
      <FormField :label="t(`${KEY}.BOARD_TYPE`)">
        <Select
          :model-value="modelValue.boardType"
          :options="boardOptions"
          :placeholder="
            t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.SELECT_PLACEHOLDER')
          "
          class="!w-full"
          @update:model-value="set({ boardType: $event })"
        />
      </FormField>
    </div>

    <div class="flex flex-col gap-2">
      <p class="text-xs font-semibold uppercase tracking-wide text-n-slate-10">
        {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.GUESTS') }}
      </p>
      <div class="grid grid-cols-2 gap-2">
        <Input
          size="sm"
          type="number"
          min="0"
          :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.ADULTS')"
          :model-value="guests().adults"
          @update:model-value="setNested('guests', { adults: $event })"
        />
        <Input
          size="sm"
          type="number"
          min="0"
          :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.CHILDREN')"
          :model-value="guests().children"
          @update:model-value="setNested('guests', { children: $event })"
        />
      </div>
      <NamesList
        :label="t(`${KEY}.GUEST_NAMES`)"
        :model-value="guests().names || []"
        @update:model-value="setNested('guests', { names: $event })"
      />
    </div>

    <TextArea
      :model-value="modelValue.notes"
      :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.NOTES')"
      :placeholder="
        t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.NOTES_PLACEHOLDER')
      "
      :max-length="2000"
      @update:model-value="set({ notes: $event })"
    />
  </div>
</template>
